import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, HandCoins, BellRing } from "lucide-react";

const OFFER_ROLES = ["recruiter", "hiring_manager", "group_hr_head", "entity_hr_head", "super_admin"];
const STATUS_STYLES = {
  draft: "bg-secondary", sent: "bg-[#DDEAE3] text-[#133E31]", accepted: "bg-emerald-100 text-emerald-800",
  declined: "bg-red-100 text-red-700", withdrawn: "bg-gray-200 text-gray-600",
};

export default function Offers() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const candParam = params.get("candidate") || "";
  const [offers, setOffers] = useState([]);
  const [cands, setCands] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ candidate_id: "", ctc_lpa: 12, joining_date: "", designation: "", notes: "" });
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [negFor, setNegFor] = useState(null);
  const [neg, setNeg] = useState({ candidate_asked: "", hr_offered: "", note: "" });

  const addNegotiation = async (offerId) => {
    if (!neg.candidate_asked || !neg.hr_offered) return toast.error("Both amounts required");
    try {
      const existingRounds = (offers.find((o) => o.id === offerId)?.negotiation_history || []).length;
      await api.post("/automation/offers/negotiation", { offer_id: offerId, round: existingRounds + 1, ...neg });
      toast.success("Negotiation logged");
      setNegFor(null); setNeg({ candidate_asked: "", hr_offered: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendFollowUp = async (offerId) => {
    try {
      const { data } = await api.post("/automation/offers/follow-up", { offer_id: offerId });
      toast.success(`Reminder #${data.reminder_no} sent${data.escalated ? " · ESCALATED" : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = () => api.get("/offers").then((r) => setOffers(r.data));
  useEffect(() => {
    load();
    api.get("/candidates").then((r) => {
      setCands(r.data);
      const eligible = r.data.filter((c) => ["interview", "offer", "shortlisted"].includes(c.stage));
      if (eligible[0]) setForm((f) => ({ ...f, candidate_id: eligible[0].id }));
    }).catch(() => {});
  }, []);

  const create = async () => {
    const cand = cands.find((c) => c.id === form.candidate_id);
    if (!cand || !form.joining_date) return toast.error("Candidate and joining date required");
    try {
      await api.post("/offers", { ...form, requisition_id: cand.requisition_id, ctc_lpa: +form.ctc_lpa });
      toast.success("Offer drafted");
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/offers/${id}/status`, { status });
      toast.success(`Offer ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleTask = async (offerId, idx, done) => {
    try {
      await api.post(`/offers/${offerId}/onboarding-task`, { task_index: idx, done });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canManage = OFFER_ROLES.includes(user?.role);
  const isCandidate = user?.role === "candidate";

  const filteredOffers = offers.filter((o) => (!statusFilter || o.status === statusFilter) && (!candParam || o.candidate_id === candParam));
  const pages = Math.max(1, Math.ceil(filteredOffers.length / 6));
  const pageOffers = filteredOffers.slice((Math.min(page, pages) - 1) * 6, Math.min(page, pages) * 6);

  return (
    <div data-testid="offers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Close & Welcome</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1">{isCandidate ? "My Offers" : "Offers & Onboarding"}</h1>
        </div>
        {canManage && (
          <button data-testid="new-offer-button" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-[#0A251D] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
            <Plus size={14} /> New Offer
          </button>
        )}
      </div>

      {showForm && (
        <div className="border bg-white rounded-xl p-6 mt-5 grid md:grid-cols-4 gap-4" data-testid="offer-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Candidate</label>
            <select data-testid="offer-candidate-select" value={form.candidate_id} onChange={(e) => setForm({ ...form, candidate_id: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
              {cands.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.stage})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">CTC (LPA)</label>
            <input data-testid="offer-ctc-input" type="number" value={form.ctc_lpa} onChange={(e) => setForm({ ...form, ctc_lpa: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Joining Date</label>
            <input data-testid="offer-joining-input" type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Designation</label>
            <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-4 flex gap-3">
            <button data-testid="offer-save-button" onClick={create} className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Create Draft</button>
            <button onClick={() => setShowForm(false)} className="border px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-secondary transition-colors duration-150">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="offers-filter-bar">
        {["", "draft", "sent", "accepted", "declined"].map((s) => (
          <button key={s || "all"} data-testid={`offers-filter-${s || "all"}`}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full border transition-colors duration-150 ${
              statusFilter === s ? "bg-[#0A251D] text-white border-[#0A251D]" : "bg-white hover:border-[#0A251D]"}`}>
            {s || "All"} ({s ? offers.filter((o) => o.status === s).length : offers.length})
          </button>
        ))}
        {candParam && (
          <button data-testid="clear-offer-candidate-filter" onClick={() => setParams({})}
            className="text-xs font-bold uppercase tracking-wider bg-[#0A251D] text-white px-3.5 py-1.5 rounded-full hover:bg-[#133E31] transition-colors duration-150">
            Candidate: {offers.find((o) => o.candidate_id === candParam)?.candidate_name || "filtered"} ✕
          </button>
        )}
      </div>

      <div className="space-y-3 mt-4">
        {pageOffers.map((o) => {
          const doneCount = o.onboarding_tasks?.filter((t) => t.done).length || 0;
          return (
            <div key={o.id} className="border bg-white rounded-xl p-5" data-testid={`offer-card-${o.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold">
                    <Link to={`/candidates/${o.candidate_id}`} data-testid={`offer-candidate-link-${o.id}`} className="text-[#0A251D] hover:text-[#E05A47] transition-colors duration-100">{o.candidate_name}</Link>
                    {" — "}{o.designation || "Offer"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    ₹{o.ctc_lpa} LPA · Joining {o.joining_date} · by {o.created_by} ·{" "}
                    <Link to={`/requisitions/${o.requisition_id}`} className="text-[#0A251D] hover:text-[#E05A47] font-semibold transition-colors duration-100">View requisition →</Link>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${STATUS_STYLES[o.status]}`}>{o.status}</span>
                  {canManage && o.status === "sent" && (
                    <button data-testid={`offer-followup-${o.id}`} onClick={() => sendFollowUp(o.id)}
                      className="text-xs border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150 flex items-center gap-1"><BellRing size={12} /> Follow-up</button>
                  )}
                  {canManage && ["draft", "sent"].includes(o.status) && (
                    <button data-testid={`offer-neg-${o.id}`} onClick={() => setNegFor(negFor === o.id ? null : o.id)}
                      className="text-xs border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150 flex items-center gap-1"><HandCoins size={12} /> Negotiate</button>
                  )}
                  {canManage && o.status === "draft" && (
                    <button data-testid={`offer-send-${o.id}`} onClick={() => setStatus(o.id, "sent")} className="text-xs border border-[#0A251D] text-[#0A251D] px-3 py-1.5 hover:bg-[#0A251D] hover:text-white transition-colors duration-150">Send Offer</button>
                  )}
                  {(canManage || isCandidate) && o.status === "sent" && (
                    <>
                      <button data-testid={`offer-accept-${o.id}`} onClick={() => setStatus(o.id, "accepted")} className="text-xs bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700 transition-colors duration-150">Accept</button>
                      <button onClick={() => setStatus(o.id, "declined")} className="text-xs bg-red-600 text-white px-3 py-1.5 hover:bg-red-700 transition-colors duration-150">Decline</button>
                    </>
                  )}
                </div>
              </div>
              {negFor === o.id && (
                <div className="mt-4 border-t pt-4 flex flex-wrap items-end gap-3" data-testid={`neg-form-${o.id}`}>
                  <div><label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Candidate asked (LPA)</label><input data-testid="neg-asked" type="number" value={neg.candidate_asked} onChange={(e) => setNeg({ ...neg, candidate_asked: e.target.value })} className="mt-1 block border px-3 py-2 text-sm w-32" /></div>
                  <div><label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">HR offered (LPA)</label><input data-testid="neg-offered" type="number" value={neg.hr_offered} onChange={(e) => setNeg({ ...neg, hr_offered: e.target.value })} className="mt-1 block border px-3 py-2 text-sm w-32" /></div>
                  <div className="flex-1 min-w-[200px]"><label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Note</label><input value={neg.note} onChange={(e) => setNeg({ ...neg, note: e.target.value })} className="mt-1 block border px-3 py-2 text-sm w-full" /></div>
                  <button data-testid={`neg-save-${o.id}`} onClick={() => addNegotiation(o.id)} className="bg-[#0A251D] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Log Round</button>
                </div>
              )}
              {o.negotiation_history?.length > 0 && (
                <div className="mt-3 border-t pt-3" data-testid={`neg-history-${o.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold mb-2">Negotiation History ({o.negotiation_history.length})</div>
                  {o.negotiation_history.map((n, i) => (
                    <div key={i} className="text-xs py-1 border-b last:border-0">
                      <span className="font-bold text-[#0A251D]">Round {n.round}:</span> asked ₹{n.candidate_asked} LPA · offered ₹{n.hr_offered} LPA
                      {n.note && <span className="text-muted-foreground"> — {n.note}</span>}
                      <span className="text-muted-foreground"> · {n.by}</span>
                    </div>
                  ))}
                </div>
              )}
              {o.acceptance_followups?.length > 0 && (
                <div className="mt-3 border-t pt-2 text-xs" data-testid={`followups-${o.id}`}>
                  <span className="font-bold text-amber-700">⏰ {o.acceptance_followups.length} follow-up(s) sent</span>
                  {o.acceptance_followups.some((r) => r.escalated) && <span className="ml-2 font-bold text-red-700">— ESCALATED</span>}
                </div>
              )}
              {o.status === "accepted" && (
                <div className="mt-4 border-t pt-4" data-testid={`onboarding-checklist-${o.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Pre-boarding & Onboarding</div>
                    <div className="text-xs font-bold text-[#0A251D]">{doneCount}/{o.onboarding_tasks.length} complete</div>
                  </div>
                  <div className="h-1.5 bg-secondary mb-3"><div className="h-full bg-[#0A251D]" style={{ width: `${(doneCount / o.onboarding_tasks.length) * 100}%` }} /></div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {o.onboarding_tasks.map((t, i) => (
                      <label key={i} className="flex items-center gap-2 text-sm border px-3 py-2 cursor-pointer hover:border-[#0A251D] transition-colors duration-150">
                        <input type="checkbox" checked={t.done} onChange={(e) => toggleTask(o.id, i, e.target.checked)} className="accent-[#0A251D]" />
                        <span className={t.done ? "line-through text-muted-foreground" : ""}>{t.task}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filteredOffers.length === 0 && <div className="text-sm text-muted-foreground border bg-white rounded-xl p-6">No offers match this filter.</div>}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button data-testid="offers-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border bg-white px-3 py-1.5 text-xs rounded-lg disabled:opacity-40 hover:border-[#0A251D] transition-colors duration-100">Prev</button>
          <span className="text-xs text-muted-foreground">Page {Math.min(page, pages)} of {pages}</span>
          <button data-testid="offers-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border bg-white px-3 py-1.5 text-xs rounded-lg disabled:opacity-40 hover:border-[#0A251D] transition-colors duration-100">Next</button>
        </div>
      )}
    </div>
  );
}
